import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Rating, LinearProgress, Avatar, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Divider, IconButton, Paper, Grid, Alert
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import api from '../services/api';

const ProductReviews = ({ productId, productName }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [writeDialogOpen, setWriteDialogOpen] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    comment: '',
    customerName: '',
    customerEmail: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [helpfulVotes, setHelpfulVotes] = useState({});

  // 載入評論
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get(`/reviews/product/${productId}`);
        if (response.data.success) {
          setReviews(response.data.reviews || []);
          setSummary(response.data.summary || null);
        }
      } catch (error) {
        console.error('載入評論失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  // 提交評論
  const handleSubmitReview = async () => {
    if (!newReview.rating || !newReview.comment) {
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reviews', {
        product_id: productId,
        ...newReview
      });
      setWriteDialogOpen(false);
      setNewReview({ rating: 5, title: '', comment: '', customerName: '', customerEmail: '' });
      // 重新載入評論
      const response = await api.get(`/reviews/product/${productId}`);
      if (response.data.success) {
        setReviews(response.data.reviews || []);
        setSummary(response.data.summary || null);
      }
    } catch (error) {
      console.error('提交評論失敗:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 標記有幫助
  const handleHelpful = async (reviewId) => {
    if (helpfulVotes[reviewId]) return;

    try {
      await api.post(`/reviews/${reviewId}/helpful`);
      setHelpfulVotes(prev => ({ ...prev, [reviewId]: true }));
      setReviews(prev => prev.map(r =>
        r.review_id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r
      ));
    } catch (error) {
      console.error('標記有幫助失敗:', error);
    }
  };

  // 格式化日期
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 評分分佈
  const RatingDistribution = () => {
    if (!summary) return null;

    const ratings = [
      { stars: 5, count: summary.rating_5_count || 0 },
      { stars: 4, count: summary.rating_4_count || 0 },
      { stars: 3, count: summary.rating_3_count || 0 },
      { stars: 2, count: summary.rating_2_count || 0 },
      { stars: 1, count: summary.rating_1_count || 0 }
    ];

    const total = summary.total_reviews || 1;

    return (
      <Box sx={{ width: '100%' }}>
        {ratings.map((r) => (
          <Box key={r.stars} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ width: 20 }}>{r.stars}</Typography>
            <StarIcon sx={{ fontSize: 16, color: '#faaf00' }} />
            <LinearProgress
              variant="determinate"
              value={(r.count / total) * 100}
              sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#eee' }}
            />
            <Typography variant="body2" sx={{ width: 30, textAlign: 'right' }}>
              {r.count}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) {
    return <Box sx={{ py: 4, textAlign: 'center' }}>載入中...</Box>;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        商品評論
      </Typography>

      {/* 評論摘要 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" fontWeight="bold" color="primary">
                {summary?.average_rating?.toFixed(1) || '0.0'}
              </Typography>
              <Rating
                value={parseFloat(summary?.average_rating) || 0}
                precision={0.1}
                readOnly
                size="large"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {summary?.total_reviews || 0} 則評論
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <RatingDistribution />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setWriteDialogOpen(true)}
              >
                撰寫評論
              </Button>
              {summary?.verified_purchase_count > 0 && (
                <Chip
                  icon={<VerifiedIcon />}
                  label={`${summary.verified_purchase_count} 則驗證購買`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 評論列表 */}
      {reviews.length === 0 ? (
        <Alert severity="info">
          目前還沒有評論，成為第一個評論者吧！
        </Alert>
      ) : (
        <Box>
          {reviews.map((review, index) => (
            <Box key={review.review_id}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {review.customer_name?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {review.customer_name || '匿名用戶'}
                    </Typography>
                    {review.verified_purchase && (
                      <Chip
                        icon={<VerifiedIcon />}
                        label="已驗證購買"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(review.created_at)}
                    </Typography>
                  </Box>
                  {review.title && (
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      {review.title}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {review.comment}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleHelpful(review.review_id)}
                      disabled={helpfulVotes[review.review_id]}
                      color={helpfulVotes[review.review_id] ? 'primary' : 'default'}
                    >
                      {helpfulVotes[review.review_id] ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                      {review.helpful_count || 0} 人覺得有幫助
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* 撰寫評論對話框 */}
      <Dialog open={writeDialogOpen} onClose={() => setWriteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>撰寫評論</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              評分
            </Typography>
            <Rating
              value={newReview.rating}
              onChange={(e, v) => setNewReview(prev => ({ ...prev, rating: v }))}
              size="large"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="您的姓名"
              value={newReview.customerName}
              onChange={(e) => setNewReview(prev => ({ ...prev, customerName: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Email (不公開)"
              type="email"
              value={newReview.customerEmail}
              onChange={(e) => setNewReview(prev => ({ ...prev, customerEmail: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="標題 (選填)"
              value={newReview.title}
              onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="評論內容"
              multiline
              rows={4}
              value={newReview.comment}
              onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteDialogOpen(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={handleSubmitReview}
            disabled={submitting || !newReview.comment}
          >
            {submitting ? '提交中...' : '提交評論'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductReviews;
